import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { track } from '@/lib/analytics';
import { setOnboardingStepDone } from '@/lib/storage';
import { OnboardingProgress } from '@/components/OnboardingProgress';
import { Button } from '@/components';
import {
  FiDollarSign,
  FiCalendar,
  FiFileText,
  FiMessageCircle,
  FiBriefcase,
  FiCheck,
} from 'react-icons/fi';

const TOTAL_STEPS = 11;

const GOAL_OPTIONS = [
  { id: 'expenses', label: 'Track shared expenses fairly', icon: FiDollarSign },
  { id: 'calendar', label: 'Coordinate custody schedules', icon: FiCalendar },
  { id: 'records', label: 'Document everything for records', icon: FiFileText },
  {
    id: 'communication',
    label: 'Communicate without conflict',
    icon: FiMessageCircle,
  },
  {
    id: 'legal',
    label: 'Get organized for legal proceedings',
    icon: FiBriefcase,
  },
];

function GoalsPage() {
  const navigate = useNavigate();
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);

  useEffect(() => {
    track('Goal Selection Screen');
  }, []);

  const toggleGoal = (goalId: string) => {
    setSelectedGoals((prev) =>
      prev.includes(goalId)
        ? prev.filter((id) => id !== goalId)
        : [...prev, goalId]
    );
  };

  const handleContinue = () => {
    track('Goal Selection Submitted', {
      goals: selectedGoals,
      goals_count: selectedGoals.length,
    });
    setOnboardingStepDone('goals');
    navigate({ to: '/app/onboarding/situation' });
  };

  const handleSkip = () => {
    track('Goal Selection Skipped');
    setOnboardingStepDone('goals');
    navigate({ to: '/app/onboarding/situation' });
  };

  return (
    <div className='min-h-screen bg-sky-light flex flex-col'>
      <OnboardingProgress currentStep={3} totalSteps={TOTAL_STEPS} />
      <div className='flex-1 flex flex-col justify-between px-6 max-w-lg mx-auto w-full'>
        <div className='flex-1 pt-5'>
          <h1
            className='text-[28px] font-extrabold text-gray-900 text-center'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            What brings you to Harbor?
          </h1>
          <p
            className='mt-2.5 text-base text-gray-500 text-center leading-relaxed mb-6'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            Select all that apply. This helps us personalize your experience.
          </p>

          <div className='space-y-2.5'>
            {GOAL_OPTIONS.map((goal) => {
              const isSelected = selectedGoals.includes(goal.id);
              const Icon = goal.icon;
              return (
                <button
                  key={goal.id}
                  onClick={() => toggleGoal(goal.id)}
                  className={`w-full flex items-center gap-3 p-3.5 rounded-2xl border-2 transition-colors text-left ${
                    isSelected
                      ? 'border-primary bg-primary/[0.04]'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div
                    className={`w-9 h-9 rounded-[10px] flex items-center justify-center transition-colors ${
                      isSelected ? 'bg-primary' : 'bg-primary/[0.08]'
                    }`}
                  >
                    <Icon
                      className={`w-5 h-5 ${
                        isSelected ? 'text-white' : 'text-primary'
                      }`}
                    />
                  </div>
                  <span
                    className={`flex-1 text-[15px] ${
                      isSelected
                        ? 'text-gray-900 font-semibold'
                        : 'text-gray-700'
                    }`}
                    style={{
                      fontFamily:
                        "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                    }}
                  >
                    {goal.label}
                  </span>
                  <div
                    className={`w-[22px] h-[22px] rounded-md border-2 flex items-center justify-center transition-colors ${
                      isSelected
                        ? 'bg-primary border-primary'
                        : 'border-gray-200'
                    }`}
                  >
                    {isSelected && (
                      <FiCheck className='w-3.5 h-3.5 text-white' />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className='w-full pb-6 pt-4 space-y-2.5'>
          <Button
            fullWidth
            onClick={handleContinue}
            disabled={selectedGoals.length === 0}
          >
            Continue
          </Button>
          <button
            onClick={handleSkip}
            className='w-full py-3 text-center text-gray-500 font-semibold hover:text-gray-700 transition-colors'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/onboarding/goals')({
  component: GoalsPage,
});
